/**
 * SubTransaction Helper
 * Utilities for managing accounting entries (debits/credits)
 */

import {
  collection,
  doc,
  getDocs,
  query,
  where,
  writeBatch
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { SYSTEM_ACCOUNTS, getTranType } from '../config/tranTypes';
import { recordReads } from './readTelemetry';
import { getAccountsCached } from './masterDataStore';

/**
 * Ledger status normalization.
 *
 * Accounting entries (subTransactions) inherit the document's business status.
 * But every financial report (Journal, Ledger, Trial Balance, P&L, Balance
 * Sheet) and the balance helpers only recognize the word "Posted" (plus Draft
 * opening balances). So documents whose live status is a domain word rather than
 * "Posted" — Salary Advance ("Issued"), Staff Loan ("Active"), a Production
 * Order ("Completed"), or a re-saved Partial/Paid invoice — would silently
 * vanish from the books.
 *
 * These business statuses all mean "financially effective → must appear in the
 * ledger", so we stamp their entries "Posted". Draft / Planned / Cancelled keep
 * their own word and remain correctly excluded by the reports' filter.
 */
const POSTED_LEDGER_STATUSES = new Set([
  'Posted', 'Partial', 'Paid', 'Issued', 'Recovered', 'Active', 'Closed', 'Completed'
]);
export const normalizeLedgerStatus = (docStatus) =>
  POSTED_LEDGER_STATUSES.has(docStatus) ? 'Posted' : (docStatus || 'Draft');

/**
 * Preload all system accounts in a single Firestore query
 * Returns a map of accountCode -> { id, code, name, ... }
 */
// Speed (2026-09-30, measured ~1.1 s on EVERY save): most companies simply don't
// have some optional system accounts (fixed assets, salary, etc.), so the "heal"
// lookup below ran on every save and never found them. Codes confirmed missing
// are now remembered for 10 minutes. If a posting then fails to balance, the
// memory is cleared so the very next attempt looks them up live again.
const _missingCodes = new Map(); // companyId -> { at, codes:Set }
const _MISSING_TTL_MS = 10 * 60 * 1000;
/** Warm-up (screen open): resolve system accounts now so the first save doesn't wait. */
export const prewarmSystemAccounts = (companyId) => preloadSystemAccounts(companyId).then(() => undefined);
export const clearSystemAccountMisses = (companyId) => { if (companyId) _missingCodes.delete(companyId); else _missingCodes.clear(); };

const preloadSystemAccounts = async (companyId) => {
  const allCodes = Object.values(SYSTEM_ACCOUNTS);
  const codeSet = new Set(allCodes);

  // Firebase cost: build the system-account map from the device-cached accounts
  // (0 reads) instead of an `in` query on every save. Falls back to a live query
  // only if the cache is empty (e.g. very first use before the first sync).
  let cache = {};
  let cacheHadRows = false;
  try {
    const all = await getAccountsCached(companyId);
    if (all && all.length) {
      cacheHadRows = true;
      all.forEach(a => { if (a.code && codeSet.has(a.code)) cache[a.code] = a; });
    }
  } catch (e) { /* fall through to live query */ }

  // Self-heal: the device cache can be STALE — e.g. a newer system account (WHT
  // Payable) or one just created by "Repair COA" hasn't synced yet. Posting a
  // leg needs the account, and a missing one silently drops the leg → the entry
  // fails to balance. So if any required code is absent, do ONE targeted live
  // lookup for JUST the missing codes and merge them in. Healthy companies (all
  // codes already cached) pay ZERO extra reads; a broken/stale company pays a
  // tiny bounded cost only until it is fixed.
  const known = _missingCodes.get(companyId);
  const knownMissing = (known && Date.now() - known.at < _MISSING_TTL_MS) ? known.codes : null;
  const missing = allCodes.filter(code => !cache[code] && !(knownMissing && knownMissing.has(code)));
  if (missing.length > 0) {
    try {
      const accountsRef = collection(db, 'companies', companyId, 'accounts');
      // Firestore `in` allows up to 30 values per query — chunk to stay safe.
      // Speed: chunks are looked up at the same time (were one after another).
      const chunks = [];
      for (let i = 0; i < missing.length; i += 30) chunks.push(missing.slice(i, i + 30));
      const snaps = await Promise.all(chunks.map(chunk => getDocs(query(accountsRef, where('code', 'in', chunk)))));
      const stillMissing = new Set(knownMissing || []);
      missing.forEach(c => stillMissing.add(c));
      for (const snapshot of snaps) {
        recordReads(companyId, snapshot.size, 'system-accounts-heal');
        snapshot.docs.forEach(d => { stillMissing.delete(d.data().code); });
        snapshot.docs.forEach(d => {
          const data = d.data();
          // If duplicates share a code, prefer a postable/active one so posting
          // targets the real account, not a stray or deactivated twin.
          const existing = cache[data.code];
          const candidate = { id: d.id, ...data };
          if (!existing || (candidate.isActive !== false && existing.isActive === false)) {
            cache[data.code] = candidate;
          }
        });
      }
      _missingCodes.set(companyId, { at: Date.now(), codes: stillMissing });
    } catch (e) {
      // If even the live lookup fails, return whatever we have; the balance
      // check downstream will surface a clear "missing account" error.
      if (!cacheHadRows) return cache;
    }
  }
  return cache;
};

/**
 * Build a short, human-readable "Particulars" for the party (customer/vendor)
 * ledger line — an auto summary of the document + the user's note. Kept concise
 * on purpose. Returns '' when there's nothing useful (caller keeps the existing
 * description). JV/CB are skipped so their manual per-line narrations survive.
 */
function buildPartyParticulars(tranTypeCode, data) {
  if (tranTypeCode === 'JV' || tranTypeCode === 'CB') return '';
  const d = data || {};
  const lines = Array.isArray(d.lines) ? d.lines : [];
  const nItems = lines.length;
  const totalQty = lines.reduce((s, l) => s + (Number(l.quantity ?? l.qty) || 0), 0);
  const disc = Number(d.discountAmount) || 0;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const note = (d.notes || d.narration || d.remarks || '').toString().trim();
  const parts = [];

  switch (tranTypeCode) {
    case 'SI': case 'NSI': case 'PI': case 'NPI': case 'IPI': case 'PS':
      if (nItems) parts.push(`${nItems} item${nItems > 1 ? 's' : ''}`);
      if (totalQty) parts.push(`${r2(totalQty)} qty`);
      if (disc > 0) parts.push(`disc ${r2(disc)}`);
      break;
    case 'SR': case 'NSR': case 'PR': case 'NPR':
      parts.push('Return');
      if (nItems) parts.push(`${nItems} item${nItems > 1 ? 's' : ''}`);
      if (totalQty) parts.push(`${r2(totalQty)} qty`);
      break;
    case 'CR': case 'CP':
      parts.push('Cash');
      break;
    case 'BR': case 'BP':
      parts.push('Bank');
      if (d.chequeNo) parts.push(`chq ${d.chequeNo}`);
      break;
    case 'OB':
      parts.push('Opening balance');
      break;
    default:
      break;
  }

  const auto = parts.join(' · '); // " · "
  if (auto && note) return `${auto} — ${note}`; // "auto — note"
  return auto || note || '';
}

/**
 * Generate SubTransactions based on transaction type
 * IMPORTANT: Deletes existing entries for this transaction first to prevent duplicates
 */
// ACC-03 (2026-09-30): pass opts.writer (see transactionHelper createWriteCollector)
// to ADD the rows to the caller's single all-or-nothing save instead of
// committing them separately. Without a writer the behaviour is unchanged.
export const generateSubTransactions = async (companyId, transactionData, userId, opts = {}) => {
  const writer = opts.writer || null;
  const {
    transactionId,
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    status,
    manualLines, // For JV, OB, PRO (manual accounting entries)
    _skipDelete // Internal flag: skip delete on brand-new transactions
  } = transactionData;

  const tranType = getTranType(tranTypeCode);
  if (!tranType || !tranType.affectsAccounting) {
    return [];
  }

  // DELETE existing entries for this transaction first (prevents duplicates)
  // Skip on brand-new transactions (nothing to delete, saves a wasted query)
  if (transactionId && !_skipDelete && !writer) {
    await deleteSubTransactions(companyId, transactionId);
  }

  // Preload ALL system accounts in a single query (instead of N individual queries)
  const accountCache = await preloadSystemAccounts(companyId);

  const entries = [];
  const timestamp = new Date().toISOString();

  // Base entry data
  const baseEntry = {
    transactionId,
    tranTypeCode,
    docNumber,
    docDate,
    financialYearId,
    branchId: transactionData.branchId || null,
    branchName: transactionData.branchName || null,
    // Normalize so live docs (Advance/Loan/Production/etc.) post to the ledger
    // as "Posted" and show up in all financial reports. See normalizeLedgerStatus.
    status: normalizeLedgerStatus(status),
    createdAt: timestamp,
    createdBy: userId
  };

  // For manual entries (JV, OB, PRO, CB), use provided lines
  if (manualLines && manualLines.length > 0) {
    for (const line of manualLines) {
      const entry = {
        ...baseEntry,
        accountId: line.accountId,
        accountCode: line.accountCode,
        accountName: line.accountName,
        debitAmount: line.debitAmount || 0,
        creditAmount: line.creditAmount || 0,
        description: line.description || `${tranType.name} ${docNumber}`
      };
      // Carry party tagging when a manual line targets a customer/vendor account
      // (used by Cash Book party lines) so party-filtered ledgers pick it up.
      if (line.partyId) { entry.partyId = line.partyId; entry.partyType = line.partyType || null; }
      entries.push(entry);
    }
  } else {
    // Auto-generate based on transaction type (using cached accounts)
    switch (tranTypeCode) {
      case 'PI':
      case 'NPI':
        // NPI reuses PI posting. With taxAmount=0 the GST Input leg is skipped;
        // A/P, inventory, and WHT (still applicable on non-tax purchases) post normally.
        generatePurchaseInvoiceEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'SI':
      case 'NSI':
        // NSI reuses SI posting. With taxAmount=0 the GST Output leg is skipped;
        // A/R, revenue, COGS, and inventory post normally.
        generateSaleInvoiceEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'PR':
      case 'NPR':
        generatePurchaseReturnEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'SR':
      case 'NSR':
        generateSaleReturnEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'CR':
        generateCashReceiptEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'BR':
        generateBankReceiptEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'CP':
        generateCashPaymentEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'BP':
        generateBankPaymentEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'AS':
        generateAddStockEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'RS':
        generateReduceStockEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'MIP':
        generateMaterialIssueEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'PRC':
        generateProductionReceiptEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'PS':
        generatePOSSaleEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'IPI':
        generateImportPurchaseInvoiceEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'ADV':
        generateSalaryAdvanceEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'LON':
        generateStaffLoanEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'LRC':
        generateLoanRecoveryEntries(entries, baseEntry, transactionData);
        break;
      case 'PAY':
        generatePayrollRunEntries(entries, baseEntry, transactionData, accountCache);
        break;
      case 'FNF':
        generateFinalSettlementEntries(entries, baseEntry, transactionData, accountCache);
        break;
      // Manual-entry types (OB, JV, PRO, DEP, FAD) — accounting entries are built
      // by the respective form via a separate generateSubTransactions call with
      // manualLines. No auto-generation at createTransaction time is intended.
      // Silent no-op.
      case 'OB':
      case 'JV':
      case 'CB':
      case 'PRO':
      case 'DEP':
      case 'FAD':
      case 'APR':
        break;
      default:
        console.warn(`No auto-generation rules for ${tranTypeCode}`);
    }
  }

  // ── Rich Particulars on the party (customer/vendor) ledger line ──────────
  // Write a short human-readable summary (+ any user note) onto the party
  // control-account entry so the Ledger reads well WITHOUT loading the full
  // document at display time. Only the description TEXT is set here — amounts,
  // accounts and the Dr/Cr are never touched, so the accounting is unchanged.
  const partyParticulars = buildPartyParticulars(tranTypeCode, transactionData);
  if (partyParticulars) {
    entries.forEach(e => {
      const nm = e.accountName || '';
      if (nm.startsWith('Customer:') || nm.startsWith('Vendor:')) {
        e.description = partyParticulars;
      }
    });
  }

  // Balance check: Σ Dr must equal Σ Cr (within ±0.01). If not, the caller's
  // accounting is broken (likely a missing SYSTEM_ACCOUNT in the COA). Throw loud
  // so the form shows a clear error instead of silently saving imbalanced books.
  if (entries.length > 0) {
    const totalDr = entries.reduce((s, e) => s + (Number(e.debitAmount) || 0), 0);
    const totalCr = entries.reduce((s, e) => s + (Number(e.creditAmount) || 0), 0);
    if (Math.abs(totalDr - totalCr) > 0.01) {
      // Forget remembered "missing" accounts so the next attempt re-checks live
      // (e.g. right after the user runs Repair COA).
      clearSystemAccountMisses(companyId);
      // Name the actual culprit(s): a leg is skipped when its system account
      // isn't in the company's COA, which is exactly what leaves Dr ≠ Cr. List
      // the system accounts that are MISSING from the cache so the user (and
      // support) know precisely what "Repair COA" needs to create — instead of a
      // blind "something is missing".
      const FRIENDLY = {
        INVENTORY_CONTROL: 'Inventory Control', COGS: 'Cost of Goods Sold',
        GST_OUTPUT: 'GST Output (Sales Tax)', GST_INPUT: 'GST Input (Sales Tax)',
        SALES_GENERAL: 'Sales – General', WHT_PAYABLE: 'WHT Payable', WHT_RECEIVABLE: 'WHT Receivable',
        CARRIAGE_RECOVERED: 'Carriage Recovered', OTHER_CHARGES_RECOVERED: 'Other Charges Recovered',
        CARRIAGE_INWARDS: 'Carriage Inwards', OTHER_CHARGES_PURCHASE: 'Other Charges (Purchase)'
      };
      const missing = Object.entries(SYSTEM_ACCOUNTS)
        .filter(([, code]) => !accountCache[code])
        .map(([key, code]) => `${FRIENDLY[key] || key} (${code})`);
      const missingMsg = missing.length
        ? `Missing account(s) in your Chart of Accounts: ${missing.join(', ')}. `
        : `A required system account appears to be missing or was renamed to a non-standard code. `;
      throw new Error(
        `Accounting entries unbalanced for ${tranTypeCode} ${docNumber}: ` +
        `Debits ${totalDr.toFixed(2)} ≠ Credits ${totalCr.toFixed(2)} (diff ${(totalDr - totalCr).toFixed(2)}). ` +
        missingMsg +
        `Go to Master Data → Chart of Accounts → click "Repair COA" to create them, then retry.`
      );
    }
  }

  // Save all entries in a single batch write (instead of N individual addDoc calls)
  const subTransRef = collection(db, 'companies', companyId, 'subTransactions');
  const batch = writer ? null : writeBatch(db);
  const savedEntries = [];

  // Deterministic IDs (transactionId + line position) make regeneration
  // idempotent: if this runs more than once for the same transaction — a retry
  // on a flaky connection, or a double-click — it OVERWRITES the same rows
  // instead of appending new ones, so ledger lines can never pile up as
  // duplicates. Each generation call always rebuilds the full set for the
  // transaction, so positions are stable. Falls back to a random ID only if
  // transactionId is somehow missing.
  for (let i = 0; i < entries.length; i++) {
    const entry = entries[i];
    // docMonth ('YYYY-MM') enables per-month server-side aggregations
    // (dashboard Sales-vs-Expense) without a second range field.
    if (entry.docDate && !entry.docMonth) entry.docMonth = String(entry.docDate).slice(0, 7);
    const newDocRef = transactionId
      ? doc(subTransRef, `${transactionId}__s${i}`)
      : doc(subTransRef);
    if (writer) writer.set(newDocRef, entry); else batch.set(newDocRef, entry);
    savedEntries.push({ id: newDocRef.id, ...entry });
  }

  if (!writer) await batch.commit();

  // NOTE (2026-07-17, D1): we deliberately do NOT invalidate the dashboard
  // cache here anymore. Busy companies post all day, which wiped the cache on
  // every save and made the landing dashboard re-download the full year ~100×
  // a day (74% of all reads — see Reads tab). Dashboard freshness now comes
  // from its 2-hour TTL + the hard Refresh button.

  return savedEntries;
};

/**
 * Purchase Invoice Entries
 * Dr. Inventory Control - cost of goods (subtotal - discount)
 * Dr. GST Input - tax amount
 * Cr. Vendor Account - total amount (net payable after WHT)
 * Cr. WHT Payable - withholding tax amount (if applicable)
 *
 * If payment made at time of invoice:
 * Dr. Vendor Account - payment amount (reduces payable)
 * Cr. Cash/Bank - payment amount
 */
function generatePurchaseInvoiceEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, taxAmount, subtotal, discountAmount, whtAmount, lines, paymentAmount, paymentAccountId, paymentAccountName, debitAccountId, debitAccountCode, debitAccountName, transportationCharges, otherCharges } = data;

  // Calculate inventory value (subtotal minus discount, plus any MRP component
  // — when the MRP add-on is enabled, MRP is part of the purchase price and
  // therefore capitalised into inventory cost so accounting stays balanced).
  let inventoryValue = 0;
  if (lines && lines.length > 0) {
    // Sum up line costs (quantity * unitPrice - lineDiscount + mrp)
    inventoryValue = lines.reduce((sum, line) => {
      const lineGross = (line.quantity || 0) * (line.unitPrice || line.unitCost || 0);
      const lineDisc = line.discountAmount || 0;
      const lineMrp = Number(line.mrp) || 0;
      return sum + (lineGross - lineDisc + lineMrp);
    }, 0);
  } else {
    inventoryValue = (subtotal || 0) - (discountAmount || 0);
  }

  // Dr. <override or Inventory Control>
  // The user can override the debit account on the PI form. Falls back to
  // the standard Inventory Control system account.
  const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
  const piDebitAccount = debitAccountId
    ? { id: debitAccountId, code: debitAccountCode || '', name: debitAccountName || '' }
    : inventoryAccount;

  // Per-line expense accounts (module `purchases_line_expense_accounts`). A line
  // may carry `expenseAccountId` — set only on SERVICE lines, which never post
  // stock (see stockFlowHelper's validLines filter), so the ledger and inventory
  // can never diverge. This is data-driven: when NO line carries an
  // expenseAccountId (module off, or all stock lines) it collapses to exactly the
  // previous behaviour — a single debit for the whole bill.
  const lineDebitValue = (line) => {
    const lineGross = (line.quantity || 0) * (line.unitPrice || line.unitCost || 0);
    return lineGross - (line.discountAmount || 0) + (Number(line.mrp) || 0);
  };
  // Header-level (footer) discount. The line loop above builds inventoryValue
  // GROSS of this footer discount, but the vendor payable (totalAmount → Cr
  // Vendor below) is already NET of it. So the discount must also come off the
  // debit side, otherwise the voucher is unbalanced by exactly the discount
  // ("Debits ≠ Credits, diff = discount"). A purchase discount reduces the cost
  // of the goods, so it comes off the goods total; the drift-reconciliation
  // below then applies it to the footer / Inventory-Control debit row. The
  // no-lines branch already netted the discount into inventoryValue, so only
  // apply it when we summed from lines.
  const headerDiscount = (lines && lines.length > 0)
    ? Math.round((Number(discountAmount) || 0) * 100) / 100
    : 0;
  const totalDebit = Math.round((inventoryValue - headerDiscount) * 100) / 100;
  const expenseGroups = new Map();   // accountId -> { id, code, name, amount }
  let fallbackValue = 0;
  if (lines && lines.length > 0) {
    lines.forEach(line => {
      const v = lineDebitValue(line);
      if (line.expenseAccountId) {
        const g = expenseGroups.get(line.expenseAccountId) || {
          id: line.expenseAccountId,
          code: line.expenseAccountCode || '',
          name: line.expenseAccountName || '',
          amount: 0
        };
        g.amount += v;                       // several lines can share one account
        expenseGroups.set(line.expenseAccountId, g);
      } else {
        fallbackValue += v;                  // unassigned → footer/Inventory Control
      }
    });
  } else {
    fallbackValue = inventoryValue;
  }

  const debitRows = [];
  expenseGroups.forEach(g => {
    const amt = Math.round(g.amount * 100) / 100;
    if (amt !== 0) debitRows.push({ ...g, amount: amt, isFallback: false });
  });
  const fallbackAmt = Math.round(fallbackValue * 100) / 100;
  if (piDebitAccount && piDebitAccount.id && (fallbackAmt !== 0 || debitRows.length === 0)) {
    debitRows.push({
      id: piDebitAccount.id, code: piDebitAccount.code, name: piDebitAccount.name,
      amount: fallbackAmt, isFallback: true
    });
  }

  // Rounding guard: the split MUST total exactly what the single debit used to be,
  // otherwise the whole voucher fails the Σ Dr = Σ Cr balance check. Any 1-paisa
  // drift is absorbed by the fallback row (or the last row if there isn't one).
  const rowsSum = debitRows.reduce((s, r) => s + r.amount, 0);
  const drift = Math.round((totalDebit - rowsSum) * 100) / 100;
  if (drift !== 0 && debitRows.length > 0) {
    const target = debitRows.find(r => r.isFallback) || debitRows[debitRows.length - 1];
    target.amount = Math.round((target.amount + drift) * 100) / 100;
  }

  debitRows.forEach(r => {
    if (r.amount > 0) {
      entries.push({
        ...baseEntry,
        accountId: r.id,
        accountCode: r.code,
        accountName: r.name,
        debitAmount: r.amount,
        creditAmount: 0,
        description: `Debit ${r.name || 'account'} from ${baseEntry.docNumber}`
      });
    }
  });

  // Dr. GST Input
  if (taxAmount > 0) {
    const gstAccount = accountCache[SYSTEM_ACCOUNTS.GST_INPUT] || null;
    if (gstAccount) {
      entries.push({
        ...baseEntry,
        accountId: gstAccount.id,
        accountCode: gstAccount.code,
        accountName: gstAccount.name,
        debitAmount: Math.round(taxAmount * 100) / 100,
        creditAmount: 0,
        description: `GST Input on ${baseEntry.docNumber}`
      });
    }
  }

  // Dr. Charges charged by vendor (non-taxable add-ons at totals level). totalAmount
  // already includes these, so the Cr Vendor below is larger by exactly
  // (transport + other); these debits balance that.
  const transport = Math.round((Number(transportationCharges) || 0) * 100) / 100;
  if (transport > 0) {
    // Fall back to Inventory Control if the dedicated Carriage Inwards account is
    // missing — the charge is already in the Cr Vendor total, so a missing debit
    // would leave the entry unbalanced.
    const acc = accountCache[SYSTEM_ACCOUNTS.CARRIAGE_INWARDS] || accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (acc) {
      entries.push({
        ...baseEntry,
        accountId: acc.id,
        accountCode: acc.code,
        accountName: acc.name,
        debitAmount: transport,
        creditAmount: 0,
        description: `Transportation/carriage on ${baseEntry.docNumber}`
      });
    }
  }
  const otherChg = Math.round((Number(otherCharges) || 0) * 100) / 100;
  if (otherChg > 0) {
    const acc = accountCache[SYSTEM_ACCOUNTS.OTHER_CHARGES_PURCHASE] || accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (acc) {
      entries.push({
        ...baseEntry,
        accountId: acc.id,
        accountCode: acc.code,
        accountName: acc.name,
        debitAmount: otherChg,
        creditAmount: 0,
        description: `Other charges on ${baseEntry.docNumber}`
      });
    }
  }

  // Cr. WHT Payable (if applicable)
  if (whtAmount > 0) {
    const whtAccount = accountCache[SYSTEM_ACCOUNTS.WHT_PAYABLE] || null;
    if (whtAccount) {
      entries.push({
        ...baseEntry,
        accountId: whtAccount.id,
        accountCode: whtAccount.code,
        accountName: whtAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(whtAmount * 100) / 100,
        description: `WHT withheld on ${baseEntry.docNumber}`
      });
    }
  }

  // Cr. Vendor Account (net payable = total - WHT already deducted in totalAmount)
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Vendor: ${partyName}` : 'Vendor Account',
      debitAmount: 0,
      creditAmount: Math.round(totalAmount * 100) / 100,
      description: `Payable for ${baseEntry.docNumber}`
    });
  }

  // If payment made at time of invoice:
  // Dr. Vendor Account (reduce payable), Cr. Cash/Bank
  const payment = Number(paymentAmount) || 0;
  console.log('[PI SubTrans] Payment check:', { payment, paymentAccountId, partyAccountId, paymentAmount });
  if (payment > 0 && paymentAccountId && partyAccountId) {
    const vendorAccountName = partyName ? `Vendor: ${partyName}` : 'Vendor Account';

    // Dr. Vendor Account (reduce payable)
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: vendorAccountName,
      debitAmount: payment,
      creditAmount: 0,
      description: `Payment for ${baseEntry.docNumber}`
    });

    // Cr. Cash/Bank
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: 0,
      creditAmount: payment,
      description: `Payment for ${baseEntry.docNumber}`
    });
  }
}

/**
 * Sale Invoice Entries
 * Dr. Customer Account (Level 5) - total amount (receivable from customer)
 * Cr. Sales Revenue - net amount (subtotal - discount)
 * Cr. GST Output - tax amount (if applicable)
 *
 * If payment received at time of invoice:
 * Dr. Cash/Bank - payment amount
 * Cr. Customer Account - payment amount (reduces receivable)
 */
function generateSaleInvoiceEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, taxAmount, subtotal, discountAmount, whtAmount, paymentAmount, paymentAccountId, paymentAccountName, lines, creditAccountId, creditAccountCode, creditAccountName, transportationCharges, otherCharges } = data;

  // MRP add-on: per-line MRP amounts roll into Sales Revenue so the credit side
  // balances the customer-receivable debit. When MRP is unused this is 0 → no-op.
  const mrpTotal = (lines || []).reduce((sum, line) => sum + (Number(line.mrp) || 0), 0);
  const netAmount = (subtotal || 0) - (discountAmount || 0) + mrpTotal;
  const payment = Number(paymentAmount) || 0;
  const customerAccountName = partyName ? `Customer: ${partyName}` : 'Customer Account';

  // 1. Dr. Customer Account - full invoice amount (MUST be Level 5 account)
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: customerAccountName,
      debitAmount: totalAmount,
      creditAmount: 0,
      description: `Receivable for ${baseEntry.docNumber}`
    });
  }

  // 2. Cr. <override or Sales Revenue>
  // The user can override the credit account on the SI form. Falls back to
  // the standard Sales – General system account.
  if (netAmount > 0) {
    const defaultSales = accountCache[SYSTEM_ACCOUNTS.SALES_GENERAL] || null;
    const siCreditAccount = creditAccountId
      ? { id: creditAccountId, code: creditAccountCode || '', name: creditAccountName || '' }
      : defaultSales;
    if (siCreditAccount && siCreditAccount.id) {
      entries.push({
        ...baseEntry,
        accountId: siCreditAccount.id,
        accountCode: siCreditAccount.code,
        accountName: siCreditAccount.name,
        debitAmount: 0,
        creditAmount: netAmount,
        description: `Credit ${siCreditAccount.name || 'account'} from ${baseEntry.docNumber}`
      });
    }
  }

  // 3. Cr. GST Output (if tax applicable)
  if (taxAmount > 0) {
    const gstAccount = accountCache[SYSTEM_ACCOUNTS.GST_OUTPUT] || null;
    if (gstAccount) {
      entries.push({
        ...baseEntry,
        accountId: gstAccount.id,
        accountCode: gstAccount.code,
        accountName: gstAccount.name,
        debitAmount: 0,
        creditAmount: taxAmount,
        description: `GST Output on ${baseEntry.docNumber}`
      });
    }
  }

  // 3b. Cr. Charges recovered from customer (non-taxable add-ons at totals
  // level). totalAmount already includes these, so the Dr Customer above is
  // larger by exactly (transport + other); these credits balance that.
  const transport = Math.round((Number(transportationCharges) || 0) * 100) / 100;
  if (transport > 0) {
    // Fall back to the general Sales account if the dedicated Carriage Recovered
    // account is missing from COA — the charge is already in the Dr Customer
    // total, so skipping the credit would leave the entry unbalanced.
    const acc = accountCache[SYSTEM_ACCOUNTS.CARRIAGE_RECOVERED] || accountCache[SYSTEM_ACCOUNTS.SALES_GENERAL] || null;
    if (acc) {
      entries.push({
        ...baseEntry,
        accountId: acc.id,
        accountCode: acc.code,
        accountName: acc.name,
        debitAmount: 0,
        creditAmount: transport,
        description: `Transportation/carriage recovered on ${baseEntry.docNumber}`
      });
    }
  }
  const otherChg = Math.round((Number(otherCharges) || 0) * 100) / 100;
  if (otherChg > 0) {
    const acc = accountCache[SYSTEM_ACCOUNTS.OTHER_CHARGES_RECOVERED] || accountCache[SYSTEM_ACCOUNTS.SALES_GENERAL] || null;
    if (acc) {
      entries.push({
        ...baseEntry,
        accountId: acc.id,
        accountCode: acc.code,
        accountName: acc.name,
        debitAmount: 0,
        creditAmount: otherChg,
        description: `Other charges recovered on ${baseEntry.docNumber}`
      });
    }
  }

  // 3b. WHT leg — direction depends on the company setting stored on the invoice:
  //   • DEDUCT (default, withheld by customer): totalAmount is already NET of the
  //     withheld tax, and it's an advance-income-tax ASSET → Dr WHT Receivable.
  //   • ADD (advance tax collected, e.g. 236G/236H): totalAmount already INCLUDES
  //     the extra collected amount, which you owe FBR → Cr WHT Payable (liability).
  const wht = Math.round((Number(whtAmount) || 0) * 100) / 100;
  if (wht > 0) {
    const addToTotal = data.whtAddToTotal === true;
    // Add-to-total (advance tax collected) credits WHT Payable - Supplies
    // (21203002) by default; falls back to Services (21203001) only if 21203002
    // is missing from the company's COA, so the entry never goes unbalanced.
    const payableCode = data.whtPayableCode || SYSTEM_ACCOUNTS.WHT_PAYABLE_SUPPLIES;
    const acc = addToTotal
      ? (accountCache[payableCode] || accountCache[SYSTEM_ACCOUNTS.WHT_PAYABLE] || null)
      : (accountCache[SYSTEM_ACCOUNTS.WHT_RECEIVABLE] || null);
    if (acc) {
      entries.push({
        ...baseEntry,
        accountId: acc.id,
        accountCode: acc.code,
        accountName: acc.name,
        debitAmount: addToTotal ? 0 : wht,
        creditAmount: addToTotal ? wht : 0,
        description: `${addToTotal ? 'Advance tax collected' : 'WHT / advance tax withheld'} on ${baseEntry.docNumber}`
      });
    }
  }

  // 4. COGS entries — Dr. COGS, Cr. Inventory Control (product lines only)
  let totalCOGS = 0;
  if (lines && lines.length > 0) {
    totalCOGS = lines.reduce((sum, line) => {
      if (line.itemType === 'service') return sum;
      const qty = line.quantity || 0;
      const cost = line.unitCost || 0;
      return sum + (qty * cost);
    }, 0);
  }

  if (totalCOGS > 0) {
    const cogsAccount = accountCache[SYSTEM_ACCOUNTS.COGS] || null;
    if (cogsAccount) {
      entries.push({
        ...baseEntry,
        accountId: cogsAccount.id,
        accountCode: cogsAccount.code,
        accountName: cogsAccount.name,
        debitAmount: Math.round(totalCOGS * 100) / 100,
        creditAmount: 0,
        description: `COGS for ${baseEntry.docNumber}`
      });
    }

    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(totalCOGS * 100) / 100,
        description: `Inventory issued ${baseEntry.docNumber}`
      });
    }
  }

  // 5. If payment received at time of invoice:
  // Dr. Cash/Bank, Cr. Customer (reduces receivable)
  console.log('[SI SubTrans] Payment check:', { payment, paymentAccountId, partyAccountId, paymentAmount });
  if (payment > 0 && paymentAccountId && partyAccountId) {
    // Dr. Cash/Bank
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: payment,
      creditAmount: 0,
      description: `Payment received for ${baseEntry.docNumber}`
    });

    // Cr. Customer Account (reduce receivable)
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: customerAccountName,
      debitAmount: 0,
      creditAmount: payment,
      description: `Payment applied for ${baseEntry.docNumber}`
    });
  }
}

/**
 * Purchase Return Entries (Debit Note)
 * Dr. Vendor Account - net amount receivable (after WHT adjustment)
 * Dr. WHT Payable - WHT reversal (if applicable)
 * Cr. Inventory Control - cost
 * Cr. GST Input - tax reversal
 */
function generatePurchaseReturnEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, taxAmount, subtotal, discountAmount, whtAmount, lines, paymentAmount, paymentAccountId, paymentAccountName } = data;

  // Calculate total cost from lines (for inventory reversal)
  let totalCost = 0;
  if (lines && lines.length > 0) {
    totalCost = lines.reduce((sum, line) => {
      // Use totalCost if available, otherwise calculate from return qty and unit price
      if (line.totalCost) return sum + line.totalCost;
      const qty = line.returnQty || line.quantity || 0;
      const price = line.unitPrice || line.unitCost || 0;
      return sum + (qty * price);
    }, 0);
  } else {
    totalCost = (subtotal || 0) - (discountAmount || 0);
  }

  // Dr. Vendor Account (reduce payable)
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Vendor: ${partyName}` : 'Vendor Account',
      debitAmount: Math.round(totalAmount * 100) / 100,
      creditAmount: 0,
      description: `Return deduction ${baseEntry.docNumber}`
    });
  }

  // Dr. WHT Payable reversal (if applicable)
  if (whtAmount > 0) {
    const whtAccount = accountCache[SYSTEM_ACCOUNTS.WHT_PAYABLE] || null;
    if (whtAccount) {
      entries.push({
        ...baseEntry,
        accountId: whtAccount.id,
        accountCode: whtAccount.code,
        accountName: whtAccount.name,
        debitAmount: Math.round(whtAmount * 100) / 100,
        creditAmount: 0,
        description: `WHT reversal on ${baseEntry.docNumber}`
      });
    }
  }

  // Cr. Inventory Control
  if (totalCost > 0) {
    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(totalCost * 100) / 100,
        description: `Inventory return ${baseEntry.docNumber}`
      });
    }
  }

  // Cr. GST Input reversal
  if (taxAmount > 0) {
    const gstAccount = accountCache[SYSTEM_ACCOUNTS.GST_INPUT] || null;
    if (gstAccount) {
      entries.push({
        ...baseEntry,
        accountId: gstAccount.id,
        accountCode: gstAccount.code,
        accountName: gstAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(taxAmount * 100) / 100,
        description: `GST Input reversal ${baseEntry.docNumber}`
      });
    }
  }

  // If refund received from vendor at time of return:
  // Dr. Cash/Bank (receive money back), Cr. Vendor Account (reduce debit balance)
  const payment = Number(paymentAmount) || 0;
  if (payment > 0 && paymentAccountId && partyAccountId) {
    const vendorAccountName = partyName ? `Vendor: ${partyName}` : 'Vendor Account';

    // Dr. Cash/Bank (receive refund)
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: payment,
      creditAmount: 0,
      description: `Refund received for ${baseEntry.docNumber}`
    });

    // Cr. Vendor Account (reduce debit from return)
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: vendorAccountName,
      debitAmount: 0,
      creditAmount: payment,
      description: `Refund received for ${baseEntry.docNumber}`
    });
  }
}

/**
 * Sale Return Entries (Credit Note)
 * Dr. Sales Returns - net amount
 * Dr. GST Output - tax reversal (if applicable)
 * Cr. Customer Account (Level 5) - total amount (reduce receivable)
 * Dr. Inventory Control (11301001) - COGS reversal (stock returned)
 * Cr. COGS (51101001) - COGS reversal
 */
function generateSaleReturnEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, taxAmount, subtotal, discountAmount, paymentAmount, paymentAccountId, paymentAccountName, lines } = data;

  const netAmount = (subtotal || 0) - (discountAmount || 0);
  const customerAccountName = partyName ? `Customer: ${partyName}` : 'Customer Account';

  // 1. Dr. Sales Returns
  if (netAmount > 0) {
    const salesReturnsAccount = accountCache[SYSTEM_ACCOUNTS.SALES_RETURNS] || null;
    if (salesReturnsAccount) {
      entries.push({
        ...baseEntry,
        accountId: salesReturnsAccount.id,
        accountCode: salesReturnsAccount.code,
        accountName: salesReturnsAccount.name,
        debitAmount: netAmount,
        creditAmount: 0,
        description: `Sales return ${baseEntry.docNumber}`
      });
    }
  }

  // 2. Dr. GST Output (reversal - if tax was applicable)
  if (taxAmount > 0) {
    const gstAccount = accountCache[SYSTEM_ACCOUNTS.GST_OUTPUT] || null;
    if (gstAccount) {
      entries.push({
        ...baseEntry,
        accountId: gstAccount.id,
        accountCode: gstAccount.code,
        accountName: gstAccount.name,
        debitAmount: taxAmount,
        creditAmount: 0,
        description: `GST Output reversal ${baseEntry.docNumber}`
      });
    }
  }

  // 3. Cr. Customer Account (MUST be Level 5 account)
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: customerAccountName,
      debitAmount: 0,
      creditAmount: totalAmount,
      description: `Credit note for ${baseEntry.docNumber}`
    });
  }

  // 4. COGS reversal — Dr. Inventory Control, Cr. COGS (stock returned; product lines only)
  let totalCOGS = 0;
  if (lines && lines.length > 0) {
    totalCOGS = lines.reduce((sum, line) => {
      if (line.itemType === 'service') return sum;
      const qty = line.quantity || 0;
      const cost = line.unitCost || 0;
      return sum + (qty * cost);
    }, 0);
  }

  if (totalCOGS > 0) {
    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: Math.round(totalCOGS * 100) / 100,
        creditAmount: 0,
        description: `Inventory returned ${baseEntry.docNumber}`
      });
    }

    const cogsAccount = accountCache[SYSTEM_ACCOUNTS.COGS] || null;
    if (cogsAccount) {
      entries.push({
        ...baseEntry,
        accountId: cogsAccount.id,
        accountCode: cogsAccount.code,
        accountName: cogsAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(totalCOGS * 100) / 100,
        description: `COGS reversal for ${baseEntry.docNumber}`
      });
    }
  }

  // 5. If refund given to customer at time of return:
  // Dr. Customer Account (reduce credit from return), Cr. Cash/Bank (pay refund)
  const payment = Number(paymentAmount) || 0;
  if (payment > 0 && paymentAccountId && partyAccountId) {
    // Dr. Customer Account (reduce credit balance)
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: customerAccountName,
      debitAmount: payment,
      creditAmount: 0,
      description: `Refund given for ${baseEntry.docNumber}`
    });

    // Cr. Cash/Bank (pay refund)
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: 0,
      creditAmount: payment,
      description: `Refund given for ${baseEntry.docNumber}`
    });
  }
}

/**
 * Cash Receipt Entries
 * Dr. Cash Account (use provided or default to Cash in Hand)
 * Cr. Customer Account
 */
function generateCashReceiptEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, cashAccountId, cashAccountCode, cashAccountName } = data;

  // Dr. Cash Account (use provided cash account or default to Cash in Hand)
  let cashAccount;
  if (cashAccountId) {
    cashAccount = { id: cashAccountId, code: cashAccountCode, name: cashAccountName };
  } else {
    cashAccount = accountCache[SYSTEM_ACCOUNTS.CASH_IN_HAND] || null;
  }

  if (cashAccount && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: cashAccount.id,
      accountCode: cashAccount.code,
      accountName: cashAccount.name,
      debitAmount: totalAmount,
      creditAmount: 0,
      description: `Cash received ${baseEntry.docNumber}`
    });
  }

  // Cr. Customer Account
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
      debitAmount: 0,
      creditAmount: totalAmount,
      description: `Receipt from customer ${baseEntry.docNumber}`
    });
  }
}

/**
 * Bank Receipt Entries
 * Dr. Bank Account
 * Cr. Customer Account
 */
function generateBankReceiptEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, bankAccountId, bankAccountCode, bankAccountName } = data;

  // Dr. Bank Account (use provided bank or default)
  let bankAccount;
  if (bankAccountId) {
    bankAccount = { id: bankAccountId, code: bankAccountCode, name: bankAccountName };
  } else {
    bankAccount = accountCache[SYSTEM_ACCOUNTS.BANK_PRIMARY] || null;
  }

  if (bankAccount && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: bankAccount.id,
      accountCode: bankAccount.code,
      accountName: bankAccount.name,
      debitAmount: totalAmount,
      creditAmount: 0,
      description: `Bank receipt ${baseEntry.docNumber}`
    });
  }

  // Cr. Customer Account
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
      debitAmount: 0,
      creditAmount: totalAmount,
      description: `Bank receipt from customer ${baseEntry.docNumber}`
    });
  }
}

/**
 * Cash Payment Entries
 * Dr. Vendor Account
 * Cr. Cash Account (use provided or default to Cash in Hand)
 */
function generateCashPaymentEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, cashAccountId, cashAccountCode, cashAccountName } = data;

  // Dr. Vendor Account
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Vendor: ${partyName}` : 'Vendor Account',
      debitAmount: totalAmount,
      creditAmount: 0,
      description: `Payment to vendor ${baseEntry.docNumber}`
    });
  }

  // Cr. Cash Account (use provided cash account or default to Cash in Hand)
  let cashAccount;
  if (cashAccountId) {
    cashAccount = { id: cashAccountId, code: cashAccountCode, name: cashAccountName };
  } else {
    cashAccount = accountCache[SYSTEM_ACCOUNTS.CASH_IN_HAND] || null;
  }

  if (cashAccount && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: cashAccount.id,
      accountCode: cashAccount.code,
      accountName: cashAccount.name,
      debitAmount: 0,
      creditAmount: totalAmount,
      description: `Cash payment ${baseEntry.docNumber}`
    });
  }
}

/**
 * Bank Payment Entries
 * Dr. Vendor Account
 * Cr. Bank Account
 */
function generateBankPaymentEntries(entries, baseEntry, data, accountCache) {
  const { partyAccountId, partyAccountCode, partyName, totalAmount, bankAccountId, bankAccountCode, bankAccountName } = data;

  // Dr. Vendor Account
  if (partyAccountId && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Vendor: ${partyName}` : 'Vendor Account',
      debitAmount: totalAmount,
      creditAmount: 0,
      description: `Bank payment to vendor ${baseEntry.docNumber}`
    });
  }

  // Cr. Bank Account
  let bankAccount;
  if (bankAccountId) {
    bankAccount = { id: bankAccountId, code: bankAccountCode, name: bankAccountName };
  } else {
    bankAccount = accountCache[SYSTEM_ACCOUNTS.BANK_PRIMARY] || null;
  }

  if (bankAccount && totalAmount > 0) {
    entries.push({
      ...baseEntry,
      accountId: bankAccount.id,
      accountCode: bankAccount.code,
      accountName: bankAccount.name,
      debitAmount: 0,
      creditAmount: totalAmount,
      description: `Bank payment ${baseEntry.docNumber}`
    });
  }
}

/**
 * Add Stock Entries
 * Dr. Inventory Control
 * Cr. Opening Balance Equity
 */
function generateAddStockEntries(entries, baseEntry, data, accountCache) {
  const { lines } = data;

  // Calculate total cost from lines
  let totalCost = 0;
  if (lines && lines.length > 0) {
    totalCost = lines.reduce((sum, line) => sum + (line.totalCost || 0), 0);
  }

  if (totalCost > 0) {
    // Dr. Inventory Control
    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: totalCost,
        creditAmount: 0,
        description: `Stock addition ${baseEntry.docNumber}`
      });
    }

    // Cr. Opening Balance Equity
    const equityAccount = accountCache[SYSTEM_ACCOUNTS.OPENING_BALANCE_EQUITY] || null;
    if (equityAccount) {
      entries.push({
        ...baseEntry,
        accountId: equityAccount.id,
        accountCode: equityAccount.code,
        accountName: equityAccount.name,
        debitAmount: 0,
        creditAmount: totalCost,
        description: `Stock addition equity ${baseEntry.docNumber}`
      });
    }
  }
}

/**
 * Reduce Stock Entries
 * Dr. Stock Adjustment
 * Cr. Inventory Control
 */
function generateReduceStockEntries(entries, baseEntry, data, accountCache) {
  const { lines } = data;

  // Calculate total cost from lines
  let totalCost = 0;
  if (lines && lines.length > 0) {
    totalCost = lines.reduce((sum, line) => sum + (line.totalCost || 0), 0);
  }

  if (totalCost > 0) {
    // Dr. Stock Adjustment
    const adjustmentAccount = accountCache[SYSTEM_ACCOUNTS.STOCK_ADJUSTMENT] || null;
    if (adjustmentAccount) {
      entries.push({
        ...baseEntry,
        accountId: adjustmentAccount.id,
        accountCode: adjustmentAccount.code,
        accountName: adjustmentAccount.name,
        debitAmount: totalCost,
        creditAmount: 0,
        description: `Stock reduction ${baseEntry.docNumber}`
      });
    }

    // Cr. Inventory Control
    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: 0,
        creditAmount: totalCost,
        description: `Stock reduction ${baseEntry.docNumber}`
      });
    }
  }
}

/**
 * Material Issue to Production (MIP) — manual production, no BOM.
 * Moves raw-material cost out of inventory and parks it in WIP.
 *   Dr. WIP - Production   = total material cost (Σ qty × unit cost)
 *   Cr. Inventory Control  = same
 */
function generateMaterialIssueEntries(entries, baseEntry, data, accountCache) {
  const { lines } = data;
  let totalCost = 0;
  if (lines && lines.length > 0) {
    totalCost = lines.reduce((sum, line) => {
      const qty = Number(line.quantity) || 0;
      const cost = Number(line.unitCost ?? line.unitPrice) || 0;
      return sum + (Number(line.totalCost) || qty * cost);
    }, 0);
  }
  totalCost = Math.round(totalCost * 100) / 100;
  if (totalCost <= 0) return;

  const wip = accountCache[SYSTEM_ACCOUNTS.WIP_INVENTORY] || null;
  const inv = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
  if (wip) {
    entries.push({
      ...baseEntry,
      accountId: wip.id, accountCode: wip.code, accountName: wip.name,
      debitAmount: totalCost, creditAmount: 0,
      description: `Materials issued to production ${baseEntry.docNumber}`
    });
  }
  if (inv) {
    entries.push({
      ...baseEntry,
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: 0, creditAmount: totalCost,
      description: `Raw materials issued ${baseEntry.docNumber}`
    });
  }
}

/**
 * Production Receipt (PRC) — manual production, no BOM.
 * Receives finished goods back, clearing WIP into finished-goods inventory.
 * Unit cost is entered manually on the form.
 *   Dr. Inventory Control  = total finished-goods cost (Σ qty × unit cost)
 *   Cr. WIP - Production    = same
 */
function generateProductionReceiptEntries(entries, baseEntry, data, accountCache) {
  const { lines } = data;
  let totalCost = 0;
  if (lines && lines.length > 0) {
    totalCost = lines.reduce((sum, line) => {
      const qty = Number(line.quantity) || 0;
      const cost = Number(line.unitCost ?? line.unitPrice) || 0;
      return sum + (Number(line.totalCost) || qty * cost);
    }, 0);
  }
  totalCost = Math.round(totalCost * 100) / 100;
  if (totalCost <= 0) return;

  const wip = accountCache[SYSTEM_ACCOUNTS.WIP_INVENTORY] || null;
  const inv = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;

  // Outsource processing charge (subcontracting): the vendor is credited (payable)
  // and the charge is already baked into the finished-goods value, so WIP only
  // clears the material portion (totalCost − charge). Guarded so it can never
  // unbalance: needs a resolved vendor account and charge ≤ finished-goods value.
  // With no charge this is a no-op and behaviour is exactly as before.
  const charge = Math.round((Number(data.outsourceCharge) || 0) * 100) / 100;
  const vendorAccId = data.outsourceVendorAccountId || null;
  const hasCharge = charge > 0 && vendorAccId && charge <= totalCost;

  // Production overheads: each is capitalized into finished goods (its amount is
  // already inside totalCost via the FG unit cost) and credited to its expense
  // account, so WIP clears only the material portion. Guarded to never unbalance:
  // skipped entirely if the overhead sum would exceed the material portion (e.g. a
  // manual FG cost that didn't include them). With no overheads it's a no-op.
  const overheads = Array.isArray(data.overheads) ? data.overheads : [];
  let overheadSum = 0;
  // Only overheads with an expense account can be credited — count just those,
  // so the WIP credit and the overhead credits always add up.
  for (const oh of overheads) { if (oh && oh.accountId) overheadSum += Math.round((Number(oh.effectiveAmount ?? oh.amount) || 0) * 100) / 100; }
  overheadSum = Math.round(overheadSum * 100) / 100;
  const hasOverhead = overheadSum > 0 && overheadSum <= Math.round((totalCost - (hasCharge ? charge : 0)) * 100) / 100;

  const wipCredit = Math.round((totalCost - (hasCharge ? charge : 0) - (hasOverhead ? overheadSum : 0)) * 100) / 100;

  if (inv) {
    entries.push({
      ...baseEntry,
      accountId: inv.id, accountCode: inv.code, accountName: inv.name,
      debitAmount: totalCost, creditAmount: 0,
      description: `Finished goods received ${baseEntry.docNumber}`
    });
  }
  if (wip && wipCredit > 0) {
    entries.push({
      ...baseEntry,
      accountId: wip.id, accountCode: wip.code, accountName: wip.name,
      debitAmount: 0, creditAmount: wipCredit,
      description: `WIP cleared to finished goods ${baseEntry.docNumber}`
    });
  }
  if (hasCharge) {
    entries.push({
      ...baseEntry,
      accountId: vendorAccId,
      accountCode: data.outsourceVendorAccountCode || '',
      accountName: data.outsourceVendorAccountName || 'Outsource Vendor',
      debitAmount: 0, creditAmount: charge,
      description: `Outsource charge payable ${baseEntry.docNumber}`
    });
  }
  if (hasOverhead) {
    for (const oh of overheads) {
      const amt = Math.round((Number(oh.effectiveAmount ?? oh.amount) || 0) * 100) / 100;
      if (amt <= 0 || !oh.accountId) continue;
      entries.push({
        ...baseEntry,
        accountId: oh.accountId,
        accountCode: oh.accountCode || '',
        accountName: oh.accountName || 'Overhead',
        debitAmount: 0, creditAmount: amt,
        description: `${oh.expenseType || 'Overhead'} absorbed into finished goods ${baseEntry.docNumber}`
      });
    }
  }

  // Wastage write-off (manual production): raw material lost in production is
  // cleared out of WIP to a Production Wastage expense account. Additive and
  // self-balancing (Dr Wastage / Cr WIP), only posts when wastage was recorded
  // AND the wastage account exists — so it can never unbalance an existing save.
  const wastageValue = Math.round((Number(data.wastageValue) || 0) * 100) / 100;
  if (wastageValue > 0 && wip) {
    const wst = accountCache[SYSTEM_ACCOUNTS.PRODUCTION_WASTAGE] || null;
    if (wst) {
      entries.push({
        ...baseEntry,
        accountId: wst.id, accountCode: wst.code, accountName: wst.name,
        debitAmount: wastageValue, creditAmount: 0,
        description: `Production wastage written off ${baseEntry.docNumber}`
      });
      entries.push({
        ...baseEntry,
        accountId: wip.id, accountCode: wip.code, accountName: wip.name,
        debitAmount: 0, creditAmount: wastageValue,
        description: `WIP cleared (wastage) ${baseEntry.docNumber}`
      });
    }
  }
}

/**
 * POS Sale Entries
 * Handles Cash, Card, Split, and Credit payments
 *
 * Cash:   Dr. Cash in Hand → Cr. Sales + Cr. GST Output
 * Card:   Dr. Bank Account → Cr. Sales + Cr. GST Output
 * Credit: Dr. Customer Receivable → Cr. Sales + Cr. GST Output
 * Split:  Dr. Multiple Cash/Bank/Customer → Cr. Sales + Cr. GST Output
 * Always: Dr. COGS → Cr. Inventory Control
 */
function generatePOSSaleEntries(entries, baseEntry, data, accountCache) {
  const {
    totalAmount, taxAmount, subtotal, discountAmount, lines,
    posPayments, paymentMethod,
    partyAccountId, partyAccountCode, partyName
  } = data;

  const netAmount = (subtotal || 0) - (discountAmount || 0);

  // 1. DEBIT side — based on payment method
  if (posPayments && posPayments.length > 0) {
    // Split or explicit payment lines
    for (const pmt of posPayments) {
      if (pmt.amount <= 0) continue;

      if (pmt.method === 'credit' && partyAccountId) {
        // Dr. Customer Receivable
        entries.push({
          ...baseEntry,
          accountId: partyAccountId,
          accountCode: partyAccountCode,
          accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
          debitAmount: Math.round(pmt.amount * 100) / 100,
          creditAmount: 0,
          description: `POS credit sale ${baseEntry.docNumber}`
        });
      } else if (pmt.accountId) {
        // Dr. Cash or Bank account
        entries.push({
          ...baseEntry,
          accountId: pmt.accountId,
          accountCode: pmt.accountCode || null,
          accountName: pmt.accountName || 'Cash/Bank',
          debitAmount: Math.round(pmt.amount * 100) / 100,
          creditAmount: 0,
          description: `POS ${pmt.method} payment ${baseEntry.docNumber}`
        });
      }
    }
  } else if (paymentMethod === 'credit' && partyAccountId) {
    // Simple credit — Dr. Customer
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Customer: ${partyName}` : 'Customer Account',
      debitAmount: Math.round(totalAmount * 100) / 100,
      creditAmount: 0,
      description: `POS credit sale ${baseEntry.docNumber}`
    });
  } else {
    // Simple cash — Dr. Cash in Hand
    const cashAccount = accountCache[SYSTEM_ACCOUNTS.CASH_IN_HAND] || null;
    if (cashAccount && totalAmount > 0) {
      entries.push({
        ...baseEntry,
        accountId: cashAccount.id,
        accountCode: cashAccount.code,
        accountName: cashAccount.name,
        debitAmount: Math.round(totalAmount * 100) / 100,
        creditAmount: 0,
        description: `POS cash sale ${baseEntry.docNumber}`
      });
    }
  }

  // 2. Cr. Sales Revenue
  if (netAmount > 0) {
    const salesAccount = accountCache[SYSTEM_ACCOUNTS.SALES_GENERAL] || null;
    if (salesAccount) {
      entries.push({
        ...baseEntry,
        accountId: salesAccount.id,
        accountCode: salesAccount.code,
        accountName: salesAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(netAmount * 100) / 100,
        description: `POS sales ${baseEntry.docNumber}`
      });
    }
  }

  // 3. Cr. GST Output
  if (taxAmount > 0) {
    const gstAccount = accountCache[SYSTEM_ACCOUNTS.GST_OUTPUT] || null;
    if (gstAccount) {
      entries.push({
        ...baseEntry,
        accountId: gstAccount.id,
        accountCode: gstAccount.code,
        accountName: gstAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(taxAmount * 100) / 100,
        description: `GST Output on POS ${baseEntry.docNumber}`
      });
    }
  }

  // 4. COGS entries — Dr. COGS, Cr. Inventory Control (product lines only)
  let totalCOGS = 0;
  if (lines && lines.length > 0) {
    totalCOGS = lines.reduce((sum, line) => {
      if (line.itemType === 'service') return sum;
      const qty = line.quantity || 0;
      const cost = line.unitCost || 0;
      return sum + (qty * cost);
    }, 0);
  }

  if (totalCOGS > 0) {
    const cogsAccount = accountCache[SYSTEM_ACCOUNTS.COGS] || null;
    if (cogsAccount) {
      entries.push({
        ...baseEntry,
        accountId: cogsAccount.id,
        accountCode: cogsAccount.code,
        accountName: cogsAccount.name,
        debitAmount: Math.round(totalCOGS * 100) / 100,
        creditAmount: 0,
        description: `COGS for POS ${baseEntry.docNumber}`
      });
    }

    const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
    if (inventoryAccount) {
      entries.push({
        ...baseEntry,
        accountId: inventoryAccount.id,
        accountCode: inventoryAccount.code,
        accountName: inventoryAccount.name,
        debitAmount: 0,
        creditAmount: Math.round(totalCOGS * 100) / 100,
        description: `Inventory issued POS ${baseEntry.docNumber}`
      });
    }
  }
}

/**
 * Import Purchase Invoice (IPI) Entries
 *
 * Foreign vendor bill with duties, taxes, freight, and clearing charges.
 * The form pre-computes everything via landedCostHelper.computeLandedCost() and passes:
 *   - inventoryValue   : baseSubtotal + Σ capitalized charges (Dr Inventory Control)
 *   - baseSubtotal     : Σ line baseTotal (foreign × rate) — Cr Foreign Vendor
 *   - landedCharges[]  : per-charge fan-out — each row provides Cr account always,
 *                        plus Dr claim account when capitalize=false
 *
 * Posting:
 *   Dr Inventory Control            = inventoryValue
 *   Cr Foreign Vendor Account       = baseSubtotal
 *   For each capitalized charge:
 *     Cr charge.accountId           = chargeAmount       (Dr already in Inventory)
 *   For each non-capitalized charge:
 *     Dr charge.claimAccountId      = chargeAmount       (e.g. GST Input, WHT Receivable)
 *     Cr charge.accountId           = chargeAmount       (e.g. Customs Sales Tax Payable)
 *
 * Balance: Σ Dr = baseSubtotal + capitalizedTotal + nonCapitalizedTotal
 *          Σ Cr = baseSubtotal + capitalizedTotal + nonCapitalizedTotal  ✓
 */
function generateImportPurchaseInvoiceEntries(entries, baseEntry, data, accountCache) {
  const {
    partyAccountId, partyAccountCode, partyName,
    baseSubtotal,        // foreign × rate  →  Cr Vendor in PKR
    inventoryValue,      // baseSubtotal + capitalizedTotal  →  Dr Inventory Control
    landedCharges,       // array from landedCostHelper.computeLandedCost().charges
    paymentAmount, paymentAccountId, paymentAccountName
  } = data;

  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  // 1. Dr. Inventory Control — landed inventory value (foreign cost + capitalized charges)
  const inventoryAccount = accountCache[SYSTEM_ACCOUNTS.INVENTORY_CONTROL] || null;
  if (inventoryAccount && inventoryValue > 0) {
    entries.push({
      ...baseEntry,
      accountId: inventoryAccount.id,
      accountCode: inventoryAccount.code,
      accountName: inventoryAccount.name,
      debitAmount: r2(inventoryValue),
      creditAmount: 0,
      description: `Inventory (landed) ${baseEntry.docNumber}`
    });
  }

  // 2. Cr. Foreign Vendor Account — invoice value × exchange rate (in PKR)
  if (partyAccountId && baseSubtotal > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Vendor: ${partyName}` : 'Foreign Vendor',
      debitAmount: 0,
      creditAmount: r2(baseSubtotal),
      description: `Foreign vendor payable ${baseEntry.docNumber}`
    });
  }

  // 3. Each charge fans out to its own account(s)
  for (const ch of (landedCharges || [])) {
    const amt = r2(ch.chargeAmount);
    if (amt <= 0) continue;

    if (ch.capitalize) {
      // Dr leg already rolled into Inventory Control above.
      // Just credit the payable.
      entries.push({
        ...baseEntry,
        accountId: ch.accountId,
        accountCode: ch.accountCode || null,
        accountName: ch.accountName || ch.chargeName,
        debitAmount: 0,
        creditAmount: amt,
        description: `${ch.chargeName} ${baseEntry.docNumber}`
      });
    } else {
      // Non-capitalized: Dr the claim account (input/receivable), Cr the payable.
      if (ch.claimAccountId) {
        entries.push({
          ...baseEntry,
          accountId: ch.claimAccountId,
          accountCode: ch.claimAccountCode || null,
          accountName: ch.claimAccountName || ch.chargeName,
          debitAmount: amt,
          creditAmount: 0,
          description: `${ch.chargeName} claim ${baseEntry.docNumber}`
        });
      }
      entries.push({
        ...baseEntry,
        accountId: ch.accountId,
        accountCode: ch.accountCode || null,
        accountName: ch.accountName || ch.chargeName,
        debitAmount: 0,
        creditAmount: amt,
        description: `${ch.chargeName} payable ${baseEntry.docNumber}`
      });
    }
  }

  // 4. Optional: payment to foreign vendor at time of import (rare but supported)
  // Mirrors PI behavior — Dr Vendor (reduce payable), Cr Cash/Bank.
  const payment = Number(paymentAmount) || 0;
  if (payment > 0 && paymentAccountId && partyAccountId) {
    const vendorAccountName = partyName ? `Vendor: ${partyName}` : 'Foreign Vendor';
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: vendorAccountName,
      debitAmount: r2(payment),
      creditAmount: 0,
      description: `Payment for ${baseEntry.docNumber}`
    });
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: 0,
      creditAmount: r2(payment),
      description: `Payment for ${baseEntry.docNumber}`
    });
  }
}

/**
 * Salary Advance (ADV) Entries
 *
 * Cash advance against future salary. Auto-recovered from next Payroll Run.
 *   Dr Staff Account              = totalAmount  (advance now owed by staff)
 *   Cr Cash or Bank account       = totalAmount  (cash actually paid out)
 */
function generateSalaryAdvanceEntries(entries, baseEntry, data, accountCache) {
  const {
    partyAccountId, partyAccountCode, partyName, totalAmount,
    paymentAccountId, paymentAccountCode, paymentAccountName
  } = data;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const amt = r2(totalAmount);
  if (amt <= 0) return;

  // Dr. Staff Account
  if (partyAccountId) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Staff: ${partyName}` : 'Staff Account',
      debitAmount: amt,
      creditAmount: 0,
      description: `Advance to ${partyName || 'staff'} ${baseEntry.docNumber}`
    });
  }

  // Cr. Cash / Bank
  if (paymentAccountId) {
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: paymentAccountCode || null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: 0,
      creditAmount: amt,
      description: `Advance issued ${baseEntry.docNumber}`
    });
  }
}

/**
 * Staff Loan (LON) Entries
 *
 * Long-term loan disbursed to staff; recovered via monthly installments
 * deducted from each Payroll Run.
 *   Dr Staff Account              = principal  (full loan now owed by staff)
 *   Cr Cash or Bank account       = principal  (cash actually disbursed)
 *
 * Installment recoveries are posted as credits against the Staff Account
 * by Payroll Run (Phase 5). They roll into the same swing account so the
 * staff ledger naturally shows opening balance, deductions, and remaining.
 */
function generateStaffLoanEntries(entries, baseEntry, data, accountCache) {
  const {
    partyAccountId, partyAccountCode, partyName, totalAmount,
    paymentAccountId, paymentAccountCode, paymentAccountName
  } = data;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  const amt = r2(totalAmount);
  if (amt <= 0) return;

  // Dr. Staff Account — full principal
  if (partyAccountId) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Staff: ${partyName}` : 'Staff Account',
      debitAmount: amt,
      creditAmount: 0,
      description: `Loan to ${partyName || 'staff'} ${baseEntry.docNumber}`
    });
  }

  // Cr. Cash / Bank — disbursement
  if (paymentAccountId) {
    entries.push({
      ...baseEntry,
      accountId: paymentAccountId,
      accountCode: paymentAccountCode || null,
      accountName: paymentAccountName || 'Cash/Bank',
      debitAmount: 0,
      creditAmount: amt,
      description: `Loan disbursed ${baseEntry.docNumber}`
    });
  }
}

/**
 * Loan Recovery (LRC) Entries — staff pays a loan / advance back in cash or bank:
 *   Dr Cash / Bank (received into)   = amount
 *   Cr Staff Account                 = amount
 * The exact reverse of giving the loan / advance (generateStaffLoanEntries).
 */
function generateLoanRecoveryEntries(entries, baseEntry, data) {
  const {
    partyAccountId, partyAccountCode, partyName, totalAmount,
    paymentAccountId, paymentAccountCode, paymentAccountName
  } = data;
  const amt = Math.round((Number(totalAmount) || 0) * 100) / 100;
  if (amt <= 0 || !partyAccountId || !paymentAccountId) return;

  entries.push({
    ...baseEntry,
    accountId: paymentAccountId,
    accountCode: paymentAccountCode || null,
    accountName: paymentAccountName || 'Cash/Bank',
    debitAmount: amt,
    creditAmount: 0,
    description: `Loan / advance received from ${partyName || 'staff'} ${baseEntry.docNumber}`
  });
  entries.push({
    ...baseEntry,
    accountId: partyAccountId,
    accountCode: partyAccountCode,
    accountName: partyName ? `Staff: ${partyName}` : 'Staff Account',
    debitAmount: 0,
    creditAmount: amt,
    description: `Loan / advance repaid ${baseEntry.docNumber}`
  });
}

/**
 * Payroll Run (PAY) Entries
 *
 * Posts the monthly payroll batch to the ledger:
 *   Dr Salaries Expense                  = Σ adjustedGross across all staff
 *   Cr Each Staff Account                = (netPayable + loanInstallment + advanceRecovery) per staff
 *   Cr Each Statutory Deduction Account  = aggregated per deduction.accountId
 *
 * Balance check (per staff):
 *   adjustedGross = statutory + loan + advance + net
 *   So Cr (staff + statutory) = (net + loan + advance) + statutory = adjustedGross  ✓
 */
function generatePayrollRunEntries(entries, baseEntry, data, accountCache) {
  const { payrollLines, payrollMonth } = data;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;
  if (!payrollLines || payrollLines.length === 0) return;

  // 1. Dr Salaries Expense (aggregate gross)
  let totalGross = 0;
  payrollLines.forEach(line => { totalGross += Number(line.adjustedGross) || 0; });

  if (totalGross > 0) {
    const salaryAccount = accountCache[SYSTEM_ACCOUNTS.SALARY_EXPENSE] || null;
    if (salaryAccount) {
      entries.push({
        ...baseEntry,
        accountId: salaryAccount.id,
        accountCode: salaryAccount.code,
        accountName: salaryAccount.name,
        debitAmount: r2(totalGross),
        creditAmount: 0,
        description: `Payroll ${payrollMonth || 'period'} (${payrollLines.length} staff)`
      });
    }
  }

  // 2. Per staff: Cr their Staff Account for (net + loan + advance)
  payrollLines.forEach(line => {
    const staffCredit = r2(
      (Number(line.netPayable) || 0)
      + (Number(line.loanInstallment) || 0)
      + (Number(line.advanceRecovery) || 0)
    );
    if (line.staffAccountId && staffCredit > 0) {
      entries.push({
        ...baseEntry,
        accountId: line.staffAccountId,
        accountCode: line.staffAccountCode || null,
        accountName: line.staffName ? `Staff: ${line.staffName}` : 'Staff Account',
        debitAmount: 0,
        creditAmount: staffCredit,
        description: `${payrollMonth || ''} salary for ${line.staffName || ''}`.trim()
      });
    }

    // 3. Each statutory deduction → Cr its payable account
    (line.deductions || []).forEach(d => {
      const dAmt = r2(Number(d.computed ?? d.value) || 0);
      if (d.accountId && dAmt > 0) {
        entries.push({
          ...baseEntry,
          accountId: d.accountId,
          accountCode: d.accountCode || null,
          accountName: d.accountName || d.name,
          debitAmount: 0,
          creditAmount: dAmt,
          description: `${d.name} for ${line.staffName || ''} ${payrollMonth || ''}`.trim()
        });
      }
    });
  });
}

/**
 * Final Settlement (FNF) Entries
 *
 * Single-staff settlement when employee leaves. Components are manual
 * (gratuity, leave encashment, notice pay are labor-law-specific so we
 * don't auto-calculate). Outstanding loans + advances auto-pulled for
 * full recovery (closes them out).
 *
 *   Dr Salaries Expense                 = totalEarnings
 *   Cr Staff Account                    = (netPayable + loanRecovery + advanceRecovery)
 *   Cr Each fnfDeduction.accountId      = its amount
 */
function generateFinalSettlementEntries(entries, baseEntry, data, accountCache) {
  const {
    partyAccountId, partyAccountCode, partyName,
    fnfEarnings, fnfDeductions,
    loanRecovery, advanceRecovery,
    totalEarnings, netPayable
  } = data;
  const r2 = (n) => Math.round((Number(n) || 0) * 100) / 100;

  const earningsTotal = r2(Number(totalEarnings) || (fnfEarnings || []).reduce((s, e) => s + (Number(e.amount) || 0), 0));
  if (earningsTotal <= 0) return;

  // 1. Dr Salaries Expense — full earnings
  const salaryAccount = accountCache[SYSTEM_ACCOUNTS.SALARY_EXPENSE] || null;
  if (salaryAccount) {
    entries.push({
      ...baseEntry,
      accountId: salaryAccount.id,
      accountCode: salaryAccount.code,
      accountName: salaryAccount.name,
      debitAmount: earningsTotal,
      creditAmount: 0,
      description: `Final Settlement for ${partyName || 'staff'} ${baseEntry.docNumber}`
    });
  }

  // 2. Cr Staff Account = net + loan + advance (closes out their balance)
  const staffCredit = r2((Number(netPayable) || 0) + (Number(loanRecovery) || 0) + (Number(advanceRecovery) || 0));
  if (partyAccountId && staffCredit > 0) {
    entries.push({
      ...baseEntry,
      accountId: partyAccountId,
      accountCode: partyAccountCode,
      accountName: partyName ? `Staff: ${partyName}` : 'Staff Account',
      debitAmount: 0,
      creditAmount: staffCredit,
      description: `Final Settlement for ${partyName || 'staff'} — net + loan + advance`
    });
  }

  // 3. Cr each statutory / custom deduction account
  (fnfDeductions || []).forEach(d => {
    const amt = r2(Number(d.amount) || 0);
    if (d.accountId && amt > 0) {
      entries.push({
        ...baseEntry,
        accountId: d.accountId,
        accountCode: d.accountCode || null,
        accountName: d.accountName || d.name,
        debitAmount: 0,
        creditAmount: amt,
        description: `${d.name} on F&F ${baseEntry.docNumber}`
      });
    }
  });
}

/**
 * Delete all SubTransactions for a transaction
 */
/** Existing ledger row ids of a transaction (for all-or-nothing edits, ACC-03). */
export const listSubTransactionIds = async (companyId, transactionId) => {
  const snap = await getDocs(query(collection(db, 'companies', companyId, 'subTransactions'), where('transactionId', '==', transactionId)));
  return snap.docs.map(d => d.id);
};

export const deleteSubTransactions = async (companyId, transactionId) => {
  const subTransRef = collection(db, 'companies', companyId, 'subTransactions');
  const q = query(subTransRef, where('transactionId', '==', transactionId));
  const snapshot = await getDocs(q);

  const batch = writeBatch(db);
  snapshot.docs.forEach(doc => {
    batch.delete(doc.ref);
  });

  await batch.commit();

  // See generateSubTransactions: dashboard cache deliberately NOT invalidated (D1).

  return { deleted: snapshot.size };
};

/**
 * Get SubTransactions for a transaction
 */
export const getSubTransactions = async (companyId, transactionId) => {
  const subTransRef = collection(db, 'companies', companyId, 'subTransactions');
  const q = query(subTransRef, where('transactionId', '==', transactionId));
  const snapshot = await getDocs(q);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data()
  }));
};

export default {
  generateSubTransactions,
  deleteSubTransactions,
  getSubTransactions,
};
